import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Input,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import RichTextEdit from "./Editor";
import { useCallback, useRef, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  convertDurationToHHMMSS,
  formatDurationFromSecond,
  getToken,
  slateToHtml,
  getPendingImageUploadWarning,
  sanitizeXmlText,
  htmlToSlate,
  normalizeSlateContent,
} from "../../utils";
import { getFullApiUrl } from "../../config";
import { ExpiredPage } from "../ExpiredPage";
import { LoadingPage } from "../LoadingPage";

const allowedAudioTypes = {
  ".mp3": true,
  ".wav": true,
  ".ogg": true,
  ".m4a": true,
  ".aac": true,
  ".flac": true,
};

const allowedVideoTypes = {
  ".mp4": true,
};

function CircularProgressWithLabel(props) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress variant="determinate" {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{ color: "text.secondary" }}
        >
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const StyledBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
  height: "100vh",
  boxSizing: "border-box",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  [theme.breakpoints.up("md")]: {
    width: "75%",
    paddingRight: theme.spacing(2),
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: {
    width: "25%",
    paddingLeft: theme.spacing(2),
  },
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function EditorPage() {
  const token = getToken();
  const location = useLocation();
  const navigate = useNavigate();
  const { podcastId, episodeId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get('type');
  const isVideo = type === 'video';
  const isNewEpisode = episodeId === 'new';
  
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(!isNewEpisode);
  const [title, setTitle] = useState("");
  const [audio, setAudio] = useState("");
  const [audioFailed, setAudioFailed] = useState(false);
  const [, setVideo] = useState("");
  const [audioDuration, setAudioDuration] = useState("");
  const [showManualDuration, setShowManualDuration] = useState(false);
  const [publishedAt, setPublishedAt] = useState("");
  const [paid, setPaid] = useState(false);
  const [content, setContent] = useState(normalizeSlateContent([
    {
      type: "paragraph",
      children: [{ text: "" }],
    },
  ]));
  const fileType = isVideo ? "视频文件" : "音频文件";
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(10);
  const [publishType, setPublishType] = useState("now");
  const cancelTokenSourceRef = useRef(null);

  useEffect(() => {
    const fetchEpisodeData = async () => {
      if (isNewEpisode || !episodeId) {
        setLoading(false);
        return;
      }

      try {
        let response;
        if (isVideo) {
          response = await axios.get(
            getFullApiUrl("/api/v1/subscribe/get_video_with_video_id"),
            {
              params: { id: episodeId },
            }
          );
        } else {
          response = await axios.get(
            getFullApiUrl("/api/v1/subscribe/get_episode"),
            {
              params: { id: episodeId },
            }
          );
        }

        const episode = isVideo ? response.data.video : response.data.episode;
        setTitle(episode.Title || "");
        // 音频处理失败时清空音频，要求重新上传
        const failed = !isVideo && episode.AudioStatus === "failed";
        setAudioFailed(failed);
        setAudio(
          failed
            ? ""
            : isVideo
              ? episode.VideoDirectory || ""
              : episode.AudioFilename || ""
        );
        setAudioDuration(episode.Duration || "");
        setPublishedAt(episode.PublishedAt || "");
        setPaid(episode.IsPaid || false);
        const parsedContent = episode.Content ? htmlToSlate(episode.Content) : [
          {
            type: "paragraph",
            children: [{ text: "" }],
          },
        ];
        setContent(normalizeSlateContent(parsedContent));
        setPublishType(episode.PublishedAt ? "schedule" : "now");
      } catch (err) {
        console.error("Error fetching episode data:", err);
        if (err?.response?.data?.error === "token has expired") {
          setExpired(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEpisodeData();
  }, [episodeId, isNewEpisode, isVideo]);

  const handleCancelUpload = useCallback(() => {
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel("用户取消了上传");
    }
  }, []);

  if (!podcastId) {
    return <Alert severity="error">没有找到对应播客</Alert>;
  }

  const isAllowedFileType = (fileName, allowedTypes) => {
    const extension = "." + fileName.split(".").pop().toLowerCase();
    return !!allowedTypes[extension];
  };

  const handleSubmit = async () => {
    if (!audio) {
      alert("请先上传音频文件");
      return;
    }

    const imageUploadWarning = getPendingImageUploadWarning(content);
    if (imageUploadWarning) {
      alert(imageUploadWarning);
      return;
    }

    // Duration is now optional - backend will extract if not provided
    if (!audioDuration && showManualDuration) {
      alert("请输入音频时长");
      return;
    }

    //如果元素存在，则获取其值，如果值为空则使用 "0"。
    const priceElement = document.getElementById("episode-price");
    const price = priceElement ? priceElement.value || "0" : "0";
    const data = {
      podcast_id: podcastId,
      title: sanitizeXmlText(title),
      audio_file_name: audio,
      duration: audioDuration,
      content: slateToHtml(content),
      is_paid: paid,
      episode_id: !isNewEpisode ? episodeId : "",
      price: paid ? price : "0",
      published_at:
        publishType === "schedule"
          ? new Date(publishedAt).toISOString()
          : new Date().toISOString(),
    };
    console.log(data);

    setSubmitting(true);
    try {
      const response = await axios.post(
        getFullApiUrl("/api/v1/upload/episode"),
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        console.log("Episode uploaded successfully:", response.data);
        alert(isNewEpisode ? "单集发布成功！" : "单集更新成功！");
        navigate(-1);
      } else {
        console.error("Failed to upload episode:", response.data);
        alert(isNewEpisode ? "单集发布失败" : "单集更新失败");
      }
    } catch (err) {
      if (err.response?.data?.error === "token has expired") {
        setExpired(true);
      } else {
        console.error("Error uploading episode:", err);
        alert(
          (isNewEpisode ? "单集发布失败: " : "单集更新失败: ") +
            (err.response?.data?.error || err.message)
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getAudioDuration = (file) => {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      audio.preload = "metadata";

      audio.onloadedmetadata = () => {
        window.URL.revokeObjectURL(audio.src);
        const duration = audio.duration;
        const formatted = formatDurationFromSecond(duration);
        resolve(formatted);
      };

      audio.onerror = (error) => {
        window.URL.revokeObjectURL(audio.src);
        reject(error);
      };

      audio.src = URL.createObjectURL(file);
    });
  };

  const handleUploadAudio = async (files) => {
    if (files.length !== 1) {
      return;
    }
    const file = files[0];
    if (!isAllowedFileType(file.name, allowedAudioTypes)) {
      return;
    }
    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append("audio", file);
    formData.append("podcastId", podcastId);

    cancelTokenSourceRef.current = axios.CancelToken.source();

    try {
      const response = await axios.post(
        getFullApiUrl("/api/v1/upload/audio"),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setProgress(percentCompleted);
          },
          cancelToken: cancelTokenSourceRef.current.token,
          timeout: 6000000,
        }
      );

      console.log("服务器返回的文件标识符（UUID）:", response.data.filename);
      setAudio(response.data.filename);
      setAudioFailed(false);

      // Use server-provided duration if available, otherwise try client-side extraction
      if (response.data.metadata && response.data.metadata.duration) {
        console.log("Using server-provided duration:", response.data.metadata.duration);
        setAudioDuration(response.data.metadata.duration);
      } else {
        console.log("Server didn't provide duration, attempting client-side extraction");
        try {
          const duration = await getAudioDuration(file);
          setAudioDuration(convertDurationToHHMMSS(duration));
          console.log("Client-extracted duration:", duration);
        } catch (durationError) {
          console.error("Failed to extract duration client-side:", durationError);
          // Show manual duration input
          setShowManualDuration(true);
          alert("无法自动获取音频时长，请在右侧手动输入时长（格式：MM:SS 或 HH:MM:SS）");
        }
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("上传被取消");
      } else {
        console.error(err);
        if (err?.response?.data?.error === "token has expired") {
          setExpired(true);
        }
      }
    } finally {
      setUploading(false);
      setProgress(0);
      cancelTokenSourceRef.current = null;
    }
  };

  const handleUploadVideo = async (files) => {
    if (files.length !== 1) {
      return;
    }
    const file = files[0];
    if (!isAllowedFileType(file.name, allowedVideoTypes)) {
      return;
    }
    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append("video", file);
    formData.append("podcastId", podcastId);

    cancelTokenSourceRef.current = axios.CancelToken.source();

    try {
      const response = await axios.post(
        getFullApiUrl("/api/v1/upload/video"),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setProgress(percentCompleted);
          },
          cancelToken: cancelTokenSourceRef.current.token,
          timeout: 120000,
        }
      );

      console.log("服务器返回的文件标识符（UUID）:", response.data.filename);
      setVideo(response.data.filename);
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("上传被取消");
      } else {
        if (err.response.data.error === "token has expired") {
          setExpired(true);
        } else {
          console.error(err);
          alert("文件上传失败");
        }
      }
    } finally {
      setUploading(false);
      setProgress(0);
      cancelTokenSourceRef.current = null;
    }
  };

  if (expired) {
    return <ExpiredPage />;
  }

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <StyledBox>
      <LeftPanel>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="请输入单集标题"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{
            md: 2,
            mb: 2,
            "& .MuiOutlinedInput-root": { backgroundColor: "#FFFFFF" },
          }}
        />
        <RichTextEdit
          content={content}
          setContent={setContent}
          podcastId={podcastId}
        />
      </LeftPanel>
      <RightPanel>
        {audioFailed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            上次的音频处理失败，请重新上传音频文件后点击更新
          </Alert>
        )}
        <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {fileType}
          </Typography>
          {audio === "" ? (
            !uploading ? (
              <Button
                component="label"
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
              >
                上传{fileType}
                <VisuallyHiddenInput
                  type="file"
                  disabled={uploading}
                  onChange={(event) => {
                    if (isVideo) {
                      handleUploadVideo(event.target.files);
                    } else {
                      handleUploadAudio(event.target.files);
                    }
                  }}
                  multiple
                />
              </Button>
            ) : (
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <CircularProgressWithLabel value={progress} />
                </Grid>
                <Grid item xs={6}>
                  <Button variant="outlined" onClick={handleCancelUpload}>
                    取消上传
                  </Button>
                </Grid>
              </Grid>
            )
          ) : (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    setAudio("");
                  }}
                >
                  更新{fileType}
                </Button>
              </Grid>
            </Grid>
          )}
        </Paper>
        {/* Manual Duration Input - shown when auto-detection fails */}
        {showManualDuration && (
          <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              音频时长
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="例如：03:45 或 01:23:45"
              value={audioDuration}
              onChange={(e) => setAudioDuration(e.target.value)}
              helperText="请输入格式：MM:SS（分:秒）或 HH:MM:SS（时:分:秒）"
              sx={{ mb: 1 }}
            />
          </Paper>
        )}
        <Paper elevation={3} sx={{ p: 2, md: 2, marginBottom: 2 }}>
          <Typography variant="h6" gutterBottom>
            发布时间
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <RadioGroup
                row
                name="publish-type-group"
                value={publishType}
                onChange={(event) => {
                  setPublishType(event.target.value);
                }}
              >
                <FormControlLabel
                  value="now"
                  control={<Radio />}
                  label="立即发布"
                />
                <FormControlLabel
                  value="schedule"
                  control={<Radio />}
                  label="定时发布"
                />
              </RadioGroup>
            </Grid>
            {publishType === "schedule" && (
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    value={dayjs(publishedAt)}
                    onChange={(newValue) =>
                      setPublishedAt(newValue.toISOString())
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            )}
          </Grid>
        </Paper>
        <Paper elevation={3} sx={{ p: 2, md: 2, marginBottom: 2 }}>
          <Typography variant="h6" gutterBottom>
            单集类型
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <RadioGroup
                row
                name="episode-type-group"
                value={paid.toString()}
                onChange={(event) => {
                  setPaid(event.target.value === "true");
                }}
              >
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="免费"
                />
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="付费"
                />
              </RadioGroup>
            </Grid>
            <Grid item xs={6}>
              {paid && (
                <Input
                  id="episode-price"
                  startAdornment={
                    <InputAdornment position="start">¥</InputAdornment>
                  }
                  placeholder="价格"
                  size="small"
                />
              )}
            </Grid>
          </Grid>
        </Paper>
        <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            文案模版
          </Typography>
          <Button
            variant="contained"
            component="label"
            fullWidth
            sx={{ mb: 2 }}
          >
            点击设置文案模版或使用模版
            <input type="file" hidden accept="image/*" />
          </Button>
        </Paper>
        <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            单集封面
          </Typography>
          <Button
            variant="contained"
            component="label"
            fullWidth
            sx={{ mb: 2 }}
          >
            点击上传封面或使用资料库图片
            <input type="file" hidden accept="image/*" />
          </Button>
        </Paper>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting
            ? isNewEpisode
              ? "发布中..."
              : "更新中..."
            : isNewEpisode
              ? "发布"
              : "更新"}
        </Button>
      </RightPanel>
    </StyledBox>
  );
}
